import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import axiosInstance from "../utils/axiosInstance";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import weekOfYear from "dayjs/plugin/weekOfYear";
import { FaDownload, FaChevronRight, FaChevronLeft, FaChevronDown, FaEdit, FaTrashAlt, FaCheck, FaExclamationTriangle, FaFileExport, FaChartPie, FaClock, FaTimes, FaTasks, FaListUl } from "react-icons/fa";
import { FaUmbrellaBeach, FaCalendarDay, FaLungsVirus, FaBriefcase, FaEllipsisH } from "react-icons/fa";
import { UserContext } from "../utils/userContext";
import { useT } from "../contexts/TranslationContext";
import { getDisplayTextFromTranslations, displayText } from "../utils/translationDisplay";
import PhotoViewerModal from "../components/PhotoViewerModal";
import DeleteConfirmationModal from "../components/DeleteConfirmationModal";
import EditUserModal from "../components/EditUserModal";
import PageHeader from "../components/ui/PageHeader";
import PageWrapper from "../components/ui/PageWrapper";
import Button from "../components/ui/Button";
import Card from "../components/ui/Card";
import { useBackNavigation } from "../navigation/BackNavigationContext";

dayjs.extend(isoWeek);
dayjs.extend(weekOfYear);

const TimeReportsView = ({
  toggleView,
  setPrefilledDate,
  selectedUser: selectedUserProp,
  selectedMonth: selectedMonthProp,
  scrollToDate: scrollToDateProp,
  onScrollComplete,
}) => {
  const { user } = useContext(UserContext);
  const { t } = useT();
  const { setPageFallback } = useBackNavigation();

  const [selectedMonth, setSelectedMonth] = useState(
    selectedMonthProp || dayjs()
  );
  const [reports, setReports] = useState([]);
  const [flagsByReport, setFlagsByReport] = useState({});
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(
    selectedUserProp || user?.userId || ""
  );
  const [selectedUserName, setSelectedUserName] = useState("My Reports");
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [exportError, setExportError] = useState("");
  const [photoViewer, setPhotoViewer] = useState({ 
    isOpen: false, 
    photos: [], 
    reportDate: "", 
    projectName: "", 
    projectAddress: "" 
  });
  const [activeIndex, setActiveIndex] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);
  const [userToEdit, setUserToEdit] = useState(null);
  const [selectedReportId, setSelectedReportId] = useState(null);
  const [lazyReportDisplays, setLazyReportDisplays] = useState({});
  const [lazyEntryDisplays, setLazyEntryDisplays] = useState({});
  const translatedDayKeysRef = useRef(new Set());
  const translatingDayKeysRef = useRef(new Set());
  const dayObserverRef = useRef(null);

  // Export UI state
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [showProjectSubmenu, setShowProjectSubmenu] = useState(false);
  const [projects, setProjects] = useState([]);
  
  // Calendar modal state
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  
  // State to track when navigating to today
  const [shouldScrollToToday, setShouldScrollToToday] = useState(false);
  
  // State to track when navigating to a specific date
  const [shouldScrollToDate, setShouldScrollToDate] = useState(null);

  const scrollPositionRef = useRef(0);
  
  // Swipe gesture state
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  useEffect(() => {
    // Back from time reports should go to the report entry screen
    setPageFallback("/report");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedUserProp) setSelectedUser(selectedUserProp);
  }, [selectedUserProp]);

  useEffect(() => {
    if (selectedMonthProp) setSelectedMonth(selectedMonthProp);
  }, [selectedMonthProp]);

  useEffect(() => {
    if (user?.isAdmin) fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    fetchReports();
    fetchReportFlags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUser, selectedMonth]);

  const saveScrollPosition = () => {
    scrollPositionRef.current = window.scrollY || 0;
  };
  const restoreScrollPosition = () => {
    window.scrollTo(0, scrollPositionRef.current || 0);
  };

  useEffect(() => {
    if (!shouldScrollToToday && !shouldScrollToDate) {
      restoreScrollPosition();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reports]);

  // Scroll to today after reports are loaded when navigating to today
  useEffect(() => {
    if (shouldScrollToToday && !loading) {
      const today = dayjs();
      // Use setTimeout to ensure DOM is updated
      setTimeout(() => {
        scrollToDay(today);
        setShouldScrollToToday(false);
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldScrollToToday, loading, reports]);

  // Handle scrollToDate prop - update month if needed and set scroll flag
  useEffect(() => {
    if (scrollToDateProp) {
      const targetDate = dayjs(scrollToDateProp);
      // Only proceed if the date is valid
      if (!targetDate.isValid()) {
        return;
      }
      const targetMonth = targetDate.format("YYYY-MM");
      const currentMonth = selectedMonth.format("YYYY-MM");
      
      if (targetMonth !== currentMonth) {
        // Update month to the target month
        setSelectedMonth(targetDate);
      }
      // Set flag to scroll after reports are loaded
      setShouldScrollToDate(targetDate);
      // Clear the prop by calling onScrollComplete (which will clear scrollToDate in parent)
      // But we'll do this after scrolling completes
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollToDateProp]);

  // Scroll to specific date after reports are loaded
  useEffect(() => {
    if (shouldScrollToDate && !loading) {
      // Ensure the reports are for the correct month before scrolling
      const targetMonth = shouldScrollToDate.format("YYYY-MM");
      const currentMonth = selectedMonth.format("YYYY-MM");
      if (targetMonth !== currentMonth) {
        // Reports are still for a different month, wait for the correct month's reports
        return;
      }
      // Use setTimeout to ensure DOM is updated
      setTimeout(() => {
        scrollToDay(shouldScrollToDate);
        setShouldScrollToDate(null);
        // Notify parent that scrolling is complete
        if (onScrollComplete) {
          onScrollComplete();
        }
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldScrollToDate, loading, reports, selectedMonth]);

  const fetchReports = async () => {
    if (!selectedUser) return;
    try {
      setLoading(true);
      const resp = await axiosInstance.get(
        `/reports?month=${selectedMonth.format(
          "YYYY-MM"
        )}&userId=${selectedUser}`
      );
      setReports(resp.data || []);
    } catch (err) {
      console.error("Error fetching reports", err);
    } finally {
      setLoading(false);
    }
  };

  /** One request per month view, not per report, so the list stays cheap to open. */
  const fetchReportFlags = async () => {
    if (!selectedUser) return;
    try {
      const resp = await axiosInstance.get("/report-flags/mine", {
        params: { month: selectedMonth.format("YYYY-MM"), userId: selectedUser },
      });
      const byReport = {};
      for (const flag of resp.data || []) {
        const key = String(flag.reportId);
        if (!byReport[key]) byReport[key] = [];
        byReport[key].push(flag);
      }
      setFlagsByReport(byReport);
    } catch (err) {
      console.error("Error fetching report flags", err);
      setFlagsByReport({});
    }
  };

  const fetchUsers = async () => {
    try {
      const resp = await axiosInstance.get("/users");
      setUsers(resp.data.items || []);
    } catch (err) {
      console.error("Error fetching users", err);
    }
  };

  const fetchProjects = async () => {
    try {
      const resp = await axiosInstance.get("/projects");
      setProjects(resp.data.items || []);
    } catch (err) {
      console.error("Error fetching projects", err);
    }
  };

  const handleUserSelect = (u) => {
    setSelectedUser(u._id);
    setSelectedUserName(t("timeReports.userReports", { name: u.firstname }));
    setShowUserDropdown(false);
    scrollPositionRef.current = 0;
  };

  const handlePreviousMonth = () =>
    setSelectedMonth((s) => s.subtract(1, "month"));
  const handleNextMonth = () => setSelectedMonth((s) => s.add(1, "month"));

  // Swipe gesture handlers
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    touchEndX.current = 0;
    touchStartX.current = e.targetTouches[0].clientX;
  };

  const onTouchMove = (e) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const onTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    const distance = touchStartX.current - touchEndX.current;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      handleNextMonth();
    }
    if (isRightSwipe) {
      handlePreviousMonth();
    }
  };

  const openPhotoViewer = (photos, reportDate = "", projectName = "", projectAddress = "") => 
    setPhotoViewer({ isOpen: true, photos, reportDate, projectName, projectAddress });
  const closePhotoViewer = () => 
    setPhotoViewer({ isOpen: false, photos: [], reportDate: "", projectName: "", projectAddress: "" });

  const toggleActiveIndex = (id) =>
    setActiveIndex((a) => (a === id ? null : id));

  useEffect(() => {
    const onDocClick = (e) => {
      if (activeIndex !== null && !e.target.closest(".active-li")) {
        setActiveIndex(null);
      }
      if (showExportDropdown && !e.target.closest('[data-export-dropdown]')) {
        setShowExportDropdown(false);
        setShowProjectSubmenu(false);
      }
      setExportError("");
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [activeIndex, showExportDropdown]);

  const groupReportsByDate = () =>
    reports.reduce((acc, r) => {
      const d = dayjs(r.date).format("YYYY-MM-DD");
      (acc[d] ||= []).push(r);
      return acc;
    }, {});

  const getDaysInMonth = () => {
    const start = selectedMonth.startOf("month");
    const end = selectedMonth.endOf("month");
    const days = [];
    let cur = start;
    while (cur.isBefore(end) || cur.isSame(end)) {
      days.push(cur);
      cur = cur.add(1, "day");
    }
    return days;
  };

  const daysInMonth = getDaysInMonth();
  const weeksObj = daysInMonth.reduce((acc, d) => {
    const w = d.isoWeek();
    (acc[w] ||= []).push(d);
    return acc;
  }, {});
  
  // Sort weeks by the earliest date in each week (to handle year boundaries correctly)
  // Convert to array of [weekNumber, weekDays] pairs sorted by date
  const weeksArray = Object.entries(weeksObj)
    .map(([weekNum, weekDays]) => ({
      weekNum,
      weekDays,
      earliestDate: weekDays.reduce((earliest, day) => 
        !earliest || day.isBefore(earliest) ? day : earliest, null
      )
    }))
    .sort((a, b) => a.earliestDate.valueOf() - b.earliestDate.valueOf())
    .map(({ weekNum, weekDays }) => [weekNum, weekDays]);
  
  // Convert back to object for compatibility with existing code
  const weeks = Object.fromEntries(weeksArray);
  
  const groupedReports = groupReportsByDate();

  const translateDayReportsLazily = useCallback(async (dayKey) => {
    if (!dayKey) return;
    if (translatedDayKeysRef.current.has(dayKey)) return;
    if (translatingDayKeysRef.current.has(dayKey)) return;

    const dayReports = groupedReports[dayKey] || [];
    if (dayReports.length === 0) {
      translatedDayKeysRef.current.add(dayKey);
      return;
    }

    translatingDayKeysRef.current.add(dayKey);
    const preferredLanguage = user?.languages?.ui || "English";
    const nextReportDisplays = {};
    const nextEntryDisplays = {};

    const maybeTranslate = async (text, sourceLanguage) => {
      const raw = String(text || "").trim();
      if (!raw) return raw;
      const source = String(sourceLanguage || "").trim().toLowerCase();
      const target = String(preferredLanguage || "").trim().toLowerCase();
      if (source && target && source === target) return raw;
      try {
        const res = await axiosInstance.post("/reports/translate-description", {
          text: raw,
          ...(sourceLanguage ? { sourceLanguage } : {}),
        });
        return res?.data?.translatedText || raw;
      } catch (_) {
        return raw;
      }
    };

    try {
      await Promise.all(
        dayReports.map(async (report) => {
          const reportDisplay = await maybeTranslate(
            report.description,
            report.descriptionLanguage
          );
          nextReportDisplays[report._id] = reportDisplay;

          if (Array.isArray(report.taskEntries) && report.taskEntries.length > 0) {
            const entryDisplays = {};
            await Promise.all(
              report.taskEntries.map(async (entry, idx) => {
                const [titleDisplay, descriptionDisplay] = await Promise.all([
                  maybeTranslate(entry.title, entry.titleLanguage),
                  maybeTranslate(entry.description, entry.descriptionLanguage),
                ]);
                entryDisplays[idx] = { titleDisplay, descriptionDisplay };
              })
            );
            nextEntryDisplays[report._id] = entryDisplays;
          }
        })
      );

      if (Object.keys(nextReportDisplays).length > 0) {
        setLazyReportDisplays((prev) => ({ ...prev, ...nextReportDisplays }));
      }
      if (Object.keys(nextEntryDisplays).length > 0) {
        setLazyEntryDisplays((prev) => ({ ...prev, ...nextEntryDisplays }));
      }
      translatedDayKeysRef.current.add(dayKey);
    } finally {
      translatingDayKeysRef.current.delete(dayKey);
    }
  }, [groupedReports, user?.languages?.ui]);

  useEffect(() => {
    setLazyReportDisplays({});
    setLazyEntryDisplays({});
    translatedDayKeysRef.current = new Set();
    translatingDayKeysRef.current = new Set();
  }, [selectedMonth, selectedUser, reports]);

  useEffect(() => {
    if (dayObserverRef.current) {
      dayObserverRef.current.disconnect();
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const dayKey = entry.target?.getAttribute("data-day");
          if (dayKey) {
            translateDayReportsLazily(dayKey);
          }
        });
      },
      {
        root: null,
        rootMargin: "200px 0px",
        threshold: 0.01,
      }
    );

    document.querySelectorAll("[data-day]").forEach((el) => observer.observe(el));
    dayObserverRef.current = observer;

    return () => observer.disconnect();
  }, [reports, translateDayReportsLazily]);

  const calculateWeeklyHours = (weekDays) =>
    weekDays.reduce((sum, day) => {
      const key = day.format("YYYY-MM-DD");
      const dr = groupedReports[key] || [];
      return sum + dr.reduce((s, r) => s + (r.hours || 0), 0);
    }, 0);

  const calculateMonthlyHours = () =>
    reports.reduce((s, r) => s + (r.hours || 0), 0);

  // Helper function to check if a day is a working day (Monday-Friday)
  const isWorkingDay = (day) => {
    const dayOfWeek = day.day(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    return dayOfWeek >= 1 && dayOfWeek <= 5; // Monday (1) to Friday (5)
  };

  // Day label with first letter capitalized (locale day names may be lowercase, e.g. "söndag" -> "Söndag")
  const formatDayLabel = (day) => {
    const s = day.format("dddd, MMM D");
    return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
  };

  // Month/year label with first letter capitalized (e.g. "mars 2026" -> "Mars 2026")
  const formatMonthLabel = (month) => {
    const s = month.format("MMMM YYYY");
    return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
  };

  // Calculate completion statistics (using all days in denominator, but icon logic based on working days)
  const calculateCompletionStats = () => {
    const workingDays = daysInMonth.filter(isWorkingDay);
    const totalWorkingDays = workingDays.length;
    const totalDaysInMonth = daysInMonth.length;
    
    // Count all days with reports (including weekends)
    const allDaysWithReports = daysInMonth.filter((day) => {
      const key = day.format("YYYY-MM-DD");
      return groupedReports[key] && groupedReports[key].length > 0;
    }).length;
    
    // Count working days with reports
    const workingDaysWithReports = workingDays.filter((day) => {
      const key = day.format("YYYY-MM-DD");
      return groupedReports[key] && groupedReports[key].length > 0;
    }).length;
    
    const workingDaysWithoutReports = totalWorkingDays - workingDaysWithReports;
    
    // Check if all working days have reports
    const allWorkingDaysReported = totalWorkingDays > 0 && workingDaysWithReports === totalWorkingDays;
    
    // Completion percentage based on working days only
    const completionPercentage = totalWorkingDays > 0 
      ? Math.round((workingDaysWithReports / totalWorkingDays) * 100) 
      : 0;
    
    return { 
      totalDays: totalDaysInMonth, 
      daysWithReports: allDaysWithReports,
      daysWithoutReports: workingDaysWithoutReports, 
      completionPercentage,
      allWorkingDaysReported
    };
  };

  const completionStats = calculateCompletionStats();

  // Helper to check if a day has reports
  const dayHasReports = (day) => {
    const key = day.format("YYYY-MM-DD");
    return groupedReports[key] && groupedReports[key].length > 0;
  };

  // Helper function to get absence type styling
  const getAbsenceTypeStyle = (absenceReason) => {
    const styles = {
      "Holiday": {
        icon: FaUmbrellaBeach,
        bgColor: "bg-blue-50",
        textColor: "text-blue-700",
        borderColor: "border-blue-200",
        iconColor: "text-blue-600",
      },
      "Flex day": {
        icon: FaCalendarDay,
        bgColor: "bg-green-50",
        textColor: "text-green-700",
        borderColor: "border-green-200",
        iconColor: "text-green-600",
      },
      "Illness": {
        icon: FaLungsVirus,
        bgColor: "bg-orange-50",
        textColor: "text-orange-700",
        borderColor: "border-orange-200",
        iconColor: "text-orange-600",
      },
      "No work available": {
        icon: FaBriefcase,
        bgColor: "bg-gray-50",
        textColor: "text-gray-700",
        borderColor: "border-gray-200",
        iconColor: "text-gray-600",
      },
      "Other": {
        icon: FaEllipsisH,
        bgColor: "bg-purple-50",
        textColor: "text-purple-700",
        borderColor: "border-purple-200",
        iconColor: "text-purple-600",
      },
    };
    return styles[absenceReason] || styles["Other"];
  };

  const handleEdit = (report) => {
    saveScrollPosition();
    setPrefilledDate(report.date?.slice(0, 10));
    toggleView(report, { selectedUser, selectedMonth });
  };

  const handleDeleteClick = (reportId) => {
    setSelectedReportId(reportId);
    setIsModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedReportId) return;
    try {
      saveScrollPosition();
      await axiosInstance.delete(`/reports/${selectedReportId}`);
      await fetchReports();
    } catch (err) {
      console.error("Delete failed", err);
    } finally {
      // Always close the modal and reset state, even if there was an error
      setIsModalOpen(false);
      setSelectedReportId(null);
      setActiveIndex(null); // Reset active index in case it was set to the deleted report
    }
  };

  // Calendar logic: Generate calendar grid organized by ISO weeks, then by weekday columns (Monday-Friday)
  const getCalendarData = () => {
    const start = selectedMonth.startOf("month");
    const end = selectedMonth.endOf("month");
    
    // Get all days from the selected month only
    const daysInMonth = [];
    let current = start;
    while (current.isBefore(end) || current.isSame(end)) {
      daysInMonth.push(current);
      current = current.add(1, "day");
    }
    
    // Group days by ISO week number, tracking the earliest date for each week
    const weeks = {};
    const weekEarliestDate = {};
    daysInMonth.forEach(day => {
      const weekNum = day.isoWeek();
      if (!weeks[weekNum]) {
        weeks[weekNum] = {
          Sunday: null,
          Monday: null,
          Tuesday: null,
          Wednesday: null,
          Thursday: null,
          Friday: null,
          Saturday: null
        };
        weekEarliestDate[weekNum] = day;
      } else {
        // Track the earliest date in this week
        if (day.isBefore(weekEarliestDate[weekNum])) {
          weekEarliestDate[weekNum] = day;
        }
      }
      const dayOfWeek = day.day(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
      if (dayOfWeek === 0) weeks[weekNum].Sunday = day;
      else if (dayOfWeek === 1) weeks[weekNum].Monday = day;
      else if (dayOfWeek === 2) weeks[weekNum].Tuesday = day;
      else if (dayOfWeek === 3) weeks[weekNum].Wednesday = day;
      else if (dayOfWeek === 4) weeks[weekNum].Thursday = day;
      else if (dayOfWeek === 5) weeks[weekNum].Friday = day;
      else if (dayOfWeek === 6) weeks[weekNum].Saturday = day;
    });
    
    // Convert to array of weeks sorted by the earliest date in each week (chronological order)
    const sortedWeekNums = Object.keys(weeks).sort((a, b) => weekEarliestDate[a].valueOf() - weekEarliestDate[b].valueOf());
    
    const result = sortedWeekNums.map(weekNum => {
      const week = weeks[weekNum];
      return week;
    });
    
    return result;
  };

  const calendarWeeks = getCalendarData();

  // Scroll to day in list
  const scrollToDay = (day) => {
    const formattedDate = day.format("YYYY-MM-DD");
    const dayElement = document.querySelector(`[data-day="${formattedDate}"]`);
    if (dayElement) {
      dayElement.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  // Handle "Go to Today" button click
  const handleGoToToday = () => {
    const today = dayjs();
    const todayMonth = today.format("YYYY-MM");
    const currentMonth = selectedMonth.format("YYYY-MM");
    
    if (todayMonth !== currentMonth) {
      // Change month to today's month
      setSelectedMonth(today);
      setShouldScrollToToday(true);
    } else {
      // Already in the correct month, scroll directly
      scrollToDay(today);
    }
  };

  // Handle calendar day click
  const handleCalendarDayClick = (day) => {
    setIsCalendarOpen(false);
    scrollToDay(day);
  };

  // Close calendar on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isCalendarOpen) {
        setIsCalendarOpen(false);
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isCalendarOpen]);

  // Prevent body scroll when calendar is open
  useEffect(() => {
    if (isCalendarOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isCalendarOpen]);

  const handleExportToCSV = async (type, id) => {
    try {
      let endpoint = "";
      let filename = "reports.csv";
      switch (type) {
        case "own-month":
          endpoint = `exports/reports/user/${selectedUser}?datetime=${selectedMonth}`;
          filename = `user_${user.firstname}_reports_${new Date()
            .toISOString()
            .replace(/[:.]/g, "-")}.csv`;
          break;
        case "all-by-project":
          endpoint = `exports/reports/project/${id}`;
          break;
        case "all-all-projects":
          endpoint = `exports/reports/projects?datetime=${selectedMonth}`;
          break;
        case "all-all-projects-with-comment":
          endpoint = `exports/reports/projects?datetime=${selectedMonth}&includeComment=true`;
          break;
        default:
      }

      const resp = await axiosInstance.get(endpoint, { responseType: "blob" });
      setExportError("");
      const blob = new Blob([resp.data], { type: "text/csv" });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      if (err?.response?.status === 404) setExportError(t("timeReports.noReportsFound"));
      else console.error("Export failed", err);
    }
  };

  const selectedUserObj = users.find((u) => u._id === selectedUser);
  const titleText =
    !selectedUser || selectedUser === user?.userId
      ? t("timeReports.myReports")
      : selectedUserObj
        ? t("timeReports.userReports", { name: selectedUserObj.firstname })
        : selectedUserName;

  return (
    <>
      <PageHeader
        title={
          <div className="relative">
            <h1
              className="text-2xl font-bold flex items-center cursor-pointer"
              onClick={() => setShowUserDropdown((p) => !p)}
            >
              {titleText}
              {user?.isAdmin && <span className="ml-2 text-sm">▼</span>}
            </h1>

            {showUserDropdown && user?.isAdmin && (
              <div className="absolute bg-white border rounded mt-2 z-10 w-full">
                <ul>
                  {users.map((u) => (
                    <li
                      key={u._id}
                      className="p-2 hover:bg-gray-100 flex justify-between items-center"
                    >
                      <span
                        className="cursor-pointer text-base font-normal"
                        onClick={() => {
                          handleUserSelect(u);
                          setShowUserDropdown(false);
                        }}
                      >
                        {u.firstname} {u.lastname}
                      </span>
                      <button
                        className="text-sm text-blue-500 underline ml-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          setUserToEdit(u);
                          setIsEditUserModalOpen(true);
                        }}
                      >
                        {t("timeReports.edit")}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        }
        actions={
          <Button 
            variant="solid" 
            size="sm" 
            onClick={() => toggleView(null, { 
              selectedUser: user?.isAdmin && selectedUser && selectedUser !== user?.userId ? selectedUser : undefined,
              selectedMonth 
            })}
          >
            {t("timeReports.newReport")}
          </Button>
        }
      />
      <PageWrapper>
        {/* Enhanced Month Navigation */}
        <Card 
          className="mb-6 bg-gradient-to-r from-gray-50 to-white border-gray-200 relative touch-pan-y"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {/* Export Icon Button - Top Right */}
          <div className="absolute top-2 right-2 z-10" data-export-dropdown>
            {user?.isAdmin ? (
              <>
                <button
                  type="button"
                  className="flex items-center justify-center w-8 h-8 rounded-lg bg-white border border-gray-300 shadow-sm hover:bg-gray-50 hover:shadow-md transition-all duration-200"
                  onClick={() => setShowExportDropdown((p) => !p)}
                  title={t("timeReports.exportToCsv")}
                >
                  <FaDownload className="w-4 h-4 text-gray-700" />
                </button>

                {showExportDropdown && (
                  <div className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-xl z-10 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200" data-export-dropdown>
                    <ul className="py-1">
                      <li>
                        <button
                          className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                          onClick={() => {
                            handleExportToCSV("own-month");
                            setShowExportDropdown(false);
                          }}
                        >
                          <FaFileExport className="text-gray-400" />
                          {t("timeReports.exportSelectedUser")}
                        </button>
                      </li>
                      <li>
                        <button
                          className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                          onClick={() => {
                            handleExportToCSV("all-all-projects");
                            setShowExportDropdown(false);
                          }}
                        >
                          <FaFileExport className="text-gray-400" />
                          {t("timeReports.exportAllProjects")}
                        </button>
                      </li>
                      <li>
                        <button
                          className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                          onClick={() => {
                            handleExportToCSV("all-all-projects-with-comment");
                            setShowExportDropdown(false);
                          }}
                        >
                          <FaFileExport className="text-gray-400" />
                          {t("timeReports.exportAllProjectsWithComment")}
                        </button>
                      </li>
                      <li className="border-t border-gray-200 my-1"></li>
                      <li
                        className="relative group"
                        onMouseEnter={() => {
                          setShowProjectSubmenu(true);
                          fetchProjects();
                        }}
                        onMouseLeave={() => setShowProjectSubmenu(false)}
                      >
                        <button className="flex justify-between items-center w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                          <span className="flex items-center gap-2">
                            <FaFileExport className="text-gray-400" />
                            {t("timeReports.exportProject")}
                          </span>
                          <FaChevronRight className="text-gray-400 text-xs" />
                        </button>

                        {showProjectSubmenu && projects.length > 0 && (
                          <div className="absolute left-full top-0 ml-1 w-64 bg-white border border-gray-200 rounded-lg shadow-xl z-20 overflow-hidden">
                            <ul className="py-1 max-h-64 overflow-y-auto">
                              {projects.map((project) => (
                                <li key={project._id}>
                                  <button
                                    className="block w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                    onClick={() => {
                                      handleExportToCSV(
                                        "all-by-project",
                                        project._id
                                      );
                                      setShowExportDropdown(false);
                                      setShowProjectSubmenu(false);
                                    }}
                                  >
                                    {project.name}
                                  </button>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </li>
                    </ul>
                  </div>
                )}

                {exportError && (
                  <div className="absolute top-full right-0 mt-2 px-3 py-1.5 bg-red-50 border border-red-200 rounded-md shadow-sm z-10">
                    <span className="text-sm text-red-600 whitespace-nowrap">
                      {exportError}
                    </span>
                  </div>
                )}
              </>
            ) : (
              <button
                type="button"
                className="flex items-center justify-center w-8 h-8 rounded-lg bg-white border border-gray-300 shadow-sm hover:bg-gray-50 hover:shadow-md transition-all duration-200"
                onClick={() => handleExportToCSV("own-month")}
                title={t("timeReports.exportToCsv")}
              >
                <FaDownload className="w-4 h-4 text-gray-700" />
              </button>
            )}
          </div>

          <div className="flex flex-col sm:flex-row justify-center items-center gap-4 relative">
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={handlePreviousMonth}
                className="flex items-center gap-1"
              >
                <FaChevronLeft className="text-xs" />
                <span className="hidden sm:inline">{t("timeReports.previous")}</span>
              </Button>
              <div className="text-center">
                <h2 className="text-xl font-bold text-gray-900">
                  {formatMonthLabel(selectedMonth)}
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  {selectedMonth.format("YYYY")}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleNextMonth}
                className="flex items-center gap-1"
              >
                <span className="hidden sm:inline">{t("timeReports.next")}</span>
                <FaChevronRight className="text-xs" />
              </Button>
            </div>

            {/* Completion Summary */}
            <div className="flex items-center gap-3 px-4 py-3 bg-white rounded-lg border border-gray-200 shadow-sm">
              <div className="text-center min-w-[80px] flex-1">
                <div className="text-xs text-gray-500 flex items-center justify-center gap-1.5 mb-1">
                  <FaChartPie className={`text-sm ${completionStats.completionPercentage === 100 ? 'text-green-600' : 'text-gray-400'}`} />
                  {t("timeReports.completion")}
                </div>
                <div className="text-lg font-bold text-gray-900">
                  {completionStats.completionPercentage}%
                </div>
              </div>
              <div className="h-12 w-px bg-gray-300"></div>
              <div 
                className="text-center min-w-[80px] flex-1 cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => setIsCalendarOpen(true)}
              >
                <div className="text-xs text-gray-500 flex items-center justify-center gap-1.5 mb-1">
                  {completionStats.allWorkingDaysReported ? (
                    <FaCheck className="text-sm text-green-600" />
                  ) : (
                    <FaExclamationTriangle className="text-sm text-yellow-600" />
                  )}
                  {t("timeReports.days")}
                </div>
                <div className="text-lg font-bold text-gray-900">
                  {completionStats.daysWithReports}/{completionStats.totalDays}
                </div>
              </div>
              <div className="h-12 w-px bg-gray-300"></div>
              <div className="text-center min-w-[80px] flex-1">
                <div className="text-xs text-gray-500 flex items-center justify-center gap-1.5 mb-1">
                  <FaClock className="text-sm text-gray-400" />
                  {t("timeReports.hours")}
                </div>
                <div className="text-lg font-bold text-gray-900">
                  {calculateMonthlyHours()}
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Floating "Go to Today" Button */}
        <button
          onClick={handleGoToToday}
          className="fixed right-4 top-80 z-40 flex flex-col items-center justify-center gap-0.5 w-[60px] h-[60px] bg-gray-700 text-white rounded-full shadow-lg hover:bg-gray-600 transition-colors duration-200"
          title={t("timeReports.goToToday")}
        >
          <span className="text-sm font-medium leading-none">{t("timeReports.today")}</span>
          <FaChevronDown className="w-4 h-4" />
        </button>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="loader"></div>
            <p className="mt-4 text-gray-600">{t("timeReports.loadingReports")}</p>
          </div>
        ) : (
          <div className="w-full max-w-md md:max-w-7xl space-y-6">
            {Object.entries(weeks).map(([weekNumber, weekDays]) => {
              const weekHours = calculateWeeklyHours(weekDays);
              
              // Calculate completion based on all days in week
              const weekWorkingDays = weekDays.filter(isWorkingDay);
              const weekWorkingDaysCount = weekWorkingDays.length;
              
              // Count all days with reports (including weekends)
              const weekAllDaysWithReports = weekDays.filter(dayHasReports).length;
              
              // Count working days with reports
              const weekWorkingDaysWithReports = weekWorkingDays.filter(dayHasReports).length;
              
              // Completion percentage based on working days only
              const weekCompletionPercentage = weekWorkingDaysCount > 0 
                ? Math.round((weekWorkingDaysWithReports / weekWorkingDaysCount) * 100) 
                : 0;
              
              const weekAllWorkingDaysReported = weekWorkingDaysCount > 0 && weekWorkingDaysWithReports === weekWorkingDaysCount;

              return (
              <Card key={weekNumber} className="overflow-hidden border-gray-200 hover:shadow-lg transition-shadow duration-200 p-0">
                <div className="bg-gradient-to-r from-gray-50 to-white border-b border-gray-200 px-6 py-4">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">
                        {t("timeReports.week", { number: weekNumber })}
                      </h3>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {weekDays[0].format("MMM D")} - {weekDays[weekDays.length - 1].format("MMM D, YYYY")}
                      </p>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-3">
                        <div className={`flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-full ${
                          weekAllWorkingDaysReported 
                            ? "bg-green-100" 
                            : "bg-yellow-100"
                        }`}>
                          {weekAllWorkingDaysReported ? (
                            <FaCheck className="text-lg text-green-600" />
                          ) : (
                            <FaExclamationTriangle className="text-lg text-yellow-600" />
                          )}
                        </div>
                        <div>
                          <div className="text-sm text-gray-500">{t("timeReports.days")}</div>
                          <div className="text-lg font-semibold text-gray-900">
                            {weekAllDaysWithReports}/{weekDays.length}
                          </div>
                        </div>
                      </div>
                      <div className="h-10 w-px bg-gray-300"></div>
                      <div className="text-right">
                        <div className="text-sm text-gray-500">{t("timeReports.hours")}</div>
                        <div className="text-xl font-bold text-gray-900">
                          {weekHours}
                        </div>
                      </div>
                      <div className="h-10 w-px bg-gray-300"></div>
                      <div className="text-right">
                        <div className="text-sm text-gray-500">{t("timeReports.completion")}</div>
                        <div className="text-lg font-semibold text-gray-900">
                          {weekCompletionPercentage}%
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="px-6 py-6">
                  <ul className="space-y-4">
                    {weekDays.map((day) => {
                      const formattedDate = day.format("YYYY-MM-DD");
                      const dailyReports = groupedReports[formattedDate] || [];
                      const hasReports = dayHasReports(day);
                      const isToday = dayjs().format("YYYY-MM-DD") === formattedDate;

                      return (
                        <li key={day.toString()} className="flex flex-col" data-day={formattedDate}>
                          {dailyReports.length > 0 ? (
                            <div className={`-mx-3 px-4 bg-green-50 border-2 border-green-200 rounded-lg py-4 transition-all duration-200 ${isToday ? "ring-2 ring-brand-500 ring-offset-1" : ""}`}>
                              {/* Enhanced Day Header with Status Indicator */}
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-3">
                                  {hasReports ? (
                                    <div className="flex items-center justify-center w-8 h-8 bg-green-500 rounded-full">
                                      <FaCheck className="text-white text-sm" />
                                    </div>
                                  ) : (
                                    <div className="flex items-center justify-center w-8 h-8 bg-yellow-500 rounded-full">
                                      <FaExclamationTriangle className="text-white text-xs" />
                                    </div>
                                  )}
                                  <div>
                                    <div className="font-bold text-gray-900">
                                      {formatDayLabel(day)}
                                      {isToday && (
                                        <span className="ml-2 text-xs font-normal text-brand-600 bg-brand-100 px-2 py-0.5 rounded">
                                          {t("timeReports.today")}
                                        </span>
                                      )}
                                    </div>
                                    <div className="text-xs text-gray-500 mt-0.5">
                                      {dailyReports.length === 1
                                        ? t("timeReports.reportCountOne", { count: dailyReports.length })
                                        : t("timeReports.reportCountMany", { count: dailyReports.length })}
                                    </div>
                                  </div>
                                </div>
                                <div className="text-sm font-semibold text-gray-700">
                                  {dailyReports.reduce((sum, r) => sum + (r.hours || 0), 0)}{t("timeReports.hourSuffix")}
                                </div>
                              </div>
                              <ul className="space-y-3">
                              {dailyReports.map((report) => {
                                const uniqueId = `${formattedDate}-${report._id}`;
                                const preferredLang = user?.languages?.ui || "English";
                                const reportDescriptionDisplay =
                                  lazyReportDisplays[report._id] ?? report.descriptionDisplay;
                                const displayDescription = getDisplayTextFromTranslations(
                                  report.translatedDescriptions,
                                  report.descriptionLanguage,
                                  report.description,
                                  preferredLang,
                                  reportDescriptionDisplay
                                );
                                
                                const isAbsenceReport = report.absence?.isAbsent === true;
                                const absenceReason = report.absence?.absenceReason || "";
                                const absenceStyle = isAbsenceReport ? getAbsenceTypeStyle(absenceReason) : null;
                                const AbsenceIcon = absenceStyle?.icon;

                                return (
                                  <li
                                    key={report._id}
                                    onClick={() => toggleActiveIndex(uniqueId)}
                                    className={`relative group cursor-pointer bg-white border border-gray-200 rounded-lg p-4 transition-all duration-200 active-li ${
                                      activeIndex === uniqueId
                                        ? "ring-2 ring-brand-500 shadow-lg"
                                        : "hover:border-gray-300 hover:shadow-md"
                                    }`}
                                  >
                                    <div
                                      className={`transition-opacity ${
                                        activeIndex === uniqueId
                                          ? "opacity-50"
                                          : "opacity-100"
                                      }`}
                                    >
                                      <div className="flex justify-between items-start gap-4">
                                        <div className="flex-1 min-w-0">
                                          {isAbsenceReport ? (
                                            <div className="flex flex-col gap-2">
                                              <span
                                                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-semibold border w-fit ${absenceStyle.bgColor} ${absenceStyle.textColor} ${absenceStyle.borderColor}`}
                                              >
                                                {AbsenceIcon && (
                                                  <AbsenceIcon className={absenceStyle.iconColor} />
                                                )}
                                                {absenceReason}
                                              </span>
                                              {report.project?.name && (
                                                <div className="text-sm text-gray-700 font-medium">
                                                  {report.project?.name}
                                                  {report.project?.address && (
                                                    <span className="text-gray-500">
                                                      {" "}• {report.project.address}
                                                    </span>
                                                  )}
                                                </div>
                                              )}
                                            </div>
                                          ) : (
                                            <div>
                                              <div className="flex items-center gap-2 mb-1">
                                                <span className="text-lg font-bold text-gray-900">
                                                  {report.hours}
                                                </span>
                                                <span className="text-sm text-gray-500">{t("timeReports.hours")}</span>
                                              </div>
                                              <div className="text-sm text-gray-700 font-semibold">
                                                {report.project?.name}
                                              </div>
                                              {(report.project?.address || report.project?.city) && (
                                                <div className="text-sm text-gray-500 mt-0.5">
                                                  {[report.project.address, report.project.city].filter(Boolean).join(", ")}
                                                </div>
                                              )}
                                            </div>
                                          )}

                                          {(flagsByReport[String(report._id)] || []).length > 0 && (
                                            <div className="flex flex-wrap gap-1.5 mt-2">
                                              {flagsByReport[String(report._id)].map((flag) => (
                                                <span
                                                  key={flag._id}
                                                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs border ${
                                                    flag.status === "resolved"
                                                      ? "bg-green-50 text-green-700 border-green-200"
                                                      : "bg-amber-50 text-amber-700 border-amber-200"
                                                  }`}
                                                >
                                                  {t(`reportFlags.${flag.type === "missing_task" ? "missingTask" : flag.type}`)}
                                                  {" · "}
                                                  {t(`reportFlags.status${flag.status === "resolved" ? "Resolved" : "Open"}`)}
                                                </span>
                                              ))}
                                            </div>
                                          )}
                                        </div>

                                        {report.photos && report.photos.length > 0 && (
                                          <button
                                            className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-md text-sm font-medium hover:bg-blue-100 transition-colors"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              openPhotoViewer(
                                                report.photos,
                                                formattedDate,
                                                report.project?.name || "",
                                                report.project?.address || ""
                                              );
                                            }}
                                          >
                                            <span>📷</span>
                                            <span>{report.photos.length}</span>
                                          </button>
                                        )}
                                      </div>

                                      {!isAbsenceReport || absenceReason === "Other" ? (
                                        <div className="mt-3 pt-3 border-t border-gray-100 w-full">
                                          {/* General description: always show when present (or for absence "Other") */}
                                          {!isAbsenceReport && (displayDescription || report.description) && (
                                            <p className="text-sm text-gray-700 leading-relaxed">
                                              {displayDescription || report.description}
                                            </p>
                                          )}
                                          {!isAbsenceReport && report.internalNote?.trim() && (
                                            <div className="mt-2 p-2.5 rounded-lg bg-slate-50 border border-slate-200">
                                              <p className="text-xs font-medium text-slate-600 mb-0.5">
                                                {t("report.internalNote")}
                                              </p>
                                              <p className="text-sm text-slate-800 whitespace-pre-wrap">
                                                {report.internalNote}
                                              </p>
                                            </div>
                                          )}
                                          {isAbsenceReport && absenceReason === "Other" && (
                                            <p className="text-sm text-gray-700 leading-relaxed">
                                              {displayDescription || report.description || t("timeReports.noDescriptionProvided")}
                                            </p>
                                          )}
                                          {/* Divider between description and tasks */}
                                          {report.taskEntries && report.taskEntries.length > 0 && (
                                            <hr className="my-3 border-gray-200" />
                                          )}
                                          {/* Task entries */}
                                          {report.taskEntries && report.taskEntries.length > 0 && (
                                            <div className="space-y-2">
                                              {report.taskEntries.map((entry, idx) => {
                                                const entryWithLazyDisplay = {
                                                  ...entry,
                                                  ...(lazyEntryDisplays[report._id]?.[idx] || {}),
                                                };
                                                const isSubtask = !!(entry.subtaskId != null && entry.subtaskId !== "");
                                                const EntryIcon = isSubtask ? FaListUl : FaTasks;
                                                return (
                                                <div key={idx} className="text-sm">
                                                  <div className="flex items-center justify-between mb-1 gap-2">
                                                    <span className="font-medium text-gray-900 flex items-center gap-2 min-w-0">
                                                      <EntryIcon className="w-4 h-4 text-gray-500 flex-shrink-0" aria-hidden />
                                                      <span className="truncate">{displayText(entryWithLazyDisplay, "title") || entry.title || t("timeReports.taskNumber", { number: idx + 1 })}</span>
                                                    </span>
                                                    <span className="text-gray-600 font-semibold flex-shrink-0">
                                                      {entry.hours}{t("timeReports.hourSuffix")}
                                                    </span>
                                                  </div>
                                                  {(displayText(entryWithLazyDisplay, "description") || entry.description) && (
                                                    <p className="text-gray-600 text-xs mt-0.5 pl-2 border-l-2 border-gray-200">
                                                      {displayText(entryWithLazyDisplay, "description") || entry.description}
                                                    </p>
                                                  )}
                                                </div>
                                              );})}
                                            </div>
                                          )}
                                        </div>
                                      ) : null}
                                    </div>

                                    {activeIndex === uniqueId && (
                                      <div className="absolute inset-0 flex items-center justify-center bg-gray-900 bg-opacity-60 rounded-lg backdrop-blur-sm animate-in fade-in duration-200">
                                        <div className="flex items-center gap-3">
                                          <button
                                            className="flex items-center gap-2 border-2 border-blue-500 text-blue-600 px-5 py-2.5 rounded-lg bg-white hover:bg-blue-50 font-semibold transition-all duration-200 shadow-lg"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleEdit(report);
                                            }}
                                          >
                                            <FaEdit /> {t("timeReports.edit")}
                                          </button>
                                          <button
                                            className="flex items-center gap-2 border-2 border-red-500 text-red-600 px-5 py-2.5 rounded-lg bg-white hover:bg-red-50 font-semibold transition-all duration-200 shadow-lg"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleDeleteClick(report._id);
                                            }}
                                          >
                                            <FaTrashAlt /> {t("timeReports.delete")}
                                          </button>
                                        </div>
                                      </div>
                                    )}
                                  </li>
                                );
                              })}
                              </ul>
                            </div>
                          ) : (
                            /* Combined Day Header and No Report Available */
                            <div className={`-mx-3 px-4 ${isWorkingDay(day) ? "bg-yellow-50 border-2 border-yellow-200" : "bg-gray-50 border-2 border-gray-200"} rounded-lg py-4 transition-all duration-200 ${isToday ? "ring-2 ring-brand-500 ring-offset-1" : ""}`}>
                              <div className="flex items-start gap-3">
                                {isWorkingDay(day) && (
                                  <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 bg-yellow-600 rounded-full">
                                    <FaExclamationTriangle className="text-white" />
                                  </div>
                                )}
                                <div className="flex-1">
                                  <div className="flex items-center justify-between mb-2">
                                    <div>
                                      <div className="font-bold text-gray-900">
                                        {formatDayLabel(day)}
                                        {isToday && (
                                          <span className="ml-2 text-xs font-normal text-brand-600 bg-brand-100 px-2 py-0.5 rounded">
                                            {t("timeReports.today")}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  <p className={`text-sm font-semibold ${isWorkingDay(day) ? "text-yellow-900" : "text-gray-700"} mb-1`}>
                                    {t("timeReports.noReportAvailable")}
                                  </p>
                                  <p className={`text-xs ${isWorkingDay(day) ? "text-yellow-700" : "text-gray-500"} mb-3`}>
                                    {t("timeReports.noReportHint")}
                                  </p>
                                  <Button
                                    variant="solid"
                                    size="sm"
                                    onClick={() => {
                                      setPrefilledDate(formattedDate);
                                      toggleView(null, { 
                                        selectedUser: user?.isAdmin && selectedUser && selectedUser !== user?.userId ? selectedUser : undefined,
                                        selectedMonth 
                                      });
                                    }}
                                  >
                                    {t("timeReports.addReport")}
                                  </Button>
                                </div>
                              </div>
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </Card>
            );
            })}
          </div>
        )}

        {photoViewer.isOpen && (
          <PhotoViewerModal
            isOpen={photoViewer.isOpen}
            onClose={closePhotoViewer}
            photos={photoViewer.photos}
            reportDate={photoViewer.reportDate}
            projectName={photoViewer.projectName}
            projectAddress={photoViewer.projectAddress}
          />
        )}

        <DeleteConfirmationModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onConfirm={handleConfirmDelete}
        />

        <EditUserModal
          isOpen={isEditUserModalOpen}
          onClose={() => {
            setIsEditUserModalOpen(false);
            setUserToEdit(null);
          }}
          user={userToEdit}
          onUserUpdated={(updatedUser) => {
            setUsers((prev) =>
              prev.map((u) => (u._id === updatedUser._id ? updatedUser : u))
            );
            if (selectedUser === updatedUser._id) {
              setSelectedUserName(t("timeReports.userReports", { name: updatedUser.firstname }));
            }
          }}
        />

        {isCalendarOpen && createPortal(
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
            onClick={() => setIsCalendarOpen(false)}
          >
            <div 
              className="bg-white rounded-lg shadow-lg w-full max-w-4xl min-h-[444px] max-h-[90vh] overflow-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center z-10">
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handlePreviousMonth}
                    className="flex items-center gap-1"
                    aria-label={t("timeReports.previousMonth")}
                  >
                    <FaChevronLeft className="text-xs" />
                  </Button>
                  <h2 className="text-xl font-bold">
                    {formatMonthLabel(selectedMonth)}
                  </h2>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleNextMonth}
                    className="flex items-center gap-1"
                    aria-label={t("timeReports.nextMonth")}
                  >
                    <FaChevronRight className="text-xs" />
                  </Button>
                </div>
                <button
                  className="text-gray-500 hover:text-gray-700 ml-4"
                  onClick={() => setIsCalendarOpen(false)}
                  aria-label={t("timeReports.calendarClose")}
                >
                  <FaTimes className="w-6 h-6" />
                </button>
              </div>
              <div className="p-4">
                <div className="grid grid-cols-7 gap-2">
                  {[
                    { key: "timeReports.weekdayMon", id: "Monday" },
                    { key: "timeReports.weekdayTue", id: "Tuesday" },
                    { key: "timeReports.weekdayWed", id: "Wednesday" },
                    { key: "timeReports.weekdayThu", id: "Thursday" },
                    { key: "timeReports.weekdayFri", id: "Friday" },
                    { key: "timeReports.weekdaySat", id: "Saturday" },
                    { key: "timeReports.weekdaySun", id: "Sunday" },
                  ].map(({ key: dayKey, id: weekday }) => (
                    <div key={weekday} className="flex flex-col">
                      <div className="text-center font-semibold text-gray-700 py-2 border-b">
                        {t(dayKey)}
                      </div>
                      <div className="flex flex-col gap-2 mt-2">
                        {Array.from({ length: 5 }, (_, weekIdx) => {
                          const week = calendarWeeks[weekIdx];
                          const day = week ? week[weekday] : null;
                          if (!day) {
                            return (
                              <div key={`${weekday}-${weekIdx}-empty`} className="h-[64px]"></div>
                            );
                          }
                          const formattedDate = day.format("YYYY-MM-DD");
                          const isToday = dayjs().format("YYYY-MM-DD") === formattedDate;
                          const dayReports = groupedReports[formattedDate] || [];
                          const absenceReport = dayReports.find(r => r.absence?.isAbsent === true);
                          const workReports = dayReports.filter(r => !r.absence?.isAbsent);
                          const totalHours = workReports.reduce((sum, r) => sum + (r.hours || 0), 0);
                          const absenceStyle = absenceReport ? getAbsenceTypeStyle(absenceReport.absence.absenceReason) : null;
                          const AbsenceIcon = absenceStyle?.icon;
                          const hasReports = dayReports.length > 0;

                          return (
                            <button
                              key={day.toString()}
                              onClick={() => handleCalendarDayClick(day)}
                              className={`h-[64px] flex flex-col items-center justify-center p-1.5 rounded border transition-all duration-200 hover:shadow-md ${
                                isToday
                                  ? "bg-brand-50 border-brand-300 ring-2 ring-brand-500"
                                  : absenceReport && workReports.length === 0
                                    ? `${absenceStyle.bgColor} ${absenceStyle.borderColor}`
                                    : hasReports
                                      ? "bg-green-50 border-green-200 hover:bg-green-100"
                                      : "bg-gray-50 border-gray-200 hover:bg-gray-100"
                              }`}
                            >
                              <div className="text-sm font-semibold text-gray-900">
                                {day.format("D")}
                              </div>
                              <div className="mt-0.5 flex flex-col items-center gap-0.5">
                                {workReports.length > 0 ? (
                                  <span className="text-xs font-semibold text-green-700 leading-none">{totalHours}h</span>
                                ) : absenceReport ? (
                                  AbsenceIcon && <AbsenceIcon className={`text-xs ${absenceStyle.iconColor}`} />
                                ) : isWorkingDay(day) ? (
                                  <FaExclamationTriangle className="text-xs text-yellow-600" />
                                ) : null}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
      </PageWrapper>
    </>
  );
};

export default TimeReportsView;
